import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CopyIcon, ExternalLinkIcon, LayoutTemplateIcon, LinkIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useWorkspace } from '../../contexts/AuthContext';
import { createLanding, deleteLanding, duplicateLanding, setLandingStatus } from '../../services/landingService';
import { LANDING_TEMPLATES, LandingTemplateId } from '../../data/landingTemplates';
import { formatDate } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { EmptyState } from '../../components/crm/EmptyState';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Dropdown } from '../../components/ui/Dropdown';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Tag } from '../../components/ui/Tag';
import { toast } from '../../components/ui/Toast';
import { copyToClipboard, landingUrl } from '../../utils/clipboard';
const STATUS_LABEL = {
  borrador: 'Borrador',
  publicada: 'Publicada',
  despublicada: 'Despublicada'
} as const;
export function Landings() {
  const navigate = useNavigate();
  const {
    organizationId,
    landings,
    forms,
    leads
  } = useWorkspace();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [formId, setFormId] = useState('');
  const [template, setTemplate] = useState<LandingTemplateId>('caso_exito');
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<string | null>(null);
  async function handleCreate() {
    if (!name.trim()) return;
    setSaving(true);
    const landing = await createLanding(organizationId, name.trim(), formId || undefined, template);
    setSaving(false);
    setCreating(false);
    setName('');
    setFormId('');
    setTemplate('caso_exito');
    navigate(`/app/landings/${landing.id}/editor`);
  }
  return <>
      <PageHeader title="Landings" description="Páginas públicas para captar potenciales clientes." actions={<Button leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1094" data-mp="ggv9csw0bja0" />} onClick={() => setCreating(true)} data-id="element-1093" data-mp="ggd3d4jjlqow">
            Nueva landing
          </Button>} data-id="element-1092" data-mp="2h40t7o9ev3e" />

      {landings.length === 0 ? <EmptyState icon={<LayoutTemplateIcon className="h-5 w-5" data-id="element-1096" data-mp="3jxbsht1uc5a8" />} title="Todavía no tienes landings" description="Crea tu primera landing y conecta un formulario para empezar a captar potenciales clientes." action={<Button onClick={() => setCreating(true)} data-id="element-1097" data-mp="3ftxdn5pn1uyd">Crear landing</Button>} data-id="element-1095" data-mp="bffnwzpx3ar3" /> : <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-id="element-1098" data-mp="3topx2f8nfva8">
          {landings.map(landing => {
        const form = forms.find(f => f.id === landing.formId);
        const landingLeads = leads.filter(l => l.origin.landingId === landing.id);
        const hasVideo = landing.pageConfig.blocks.some(b => b.type === 'video' && b.visible);
        return <li key={landing.id} className="flex flex-col rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1099" data-mp="1bpyut1h0d2i9">
                <div className="flex items-start justify-between gap-3" data-id="element-1100" data-mp="1ghgwxsurp94t">
                  <div className="min-w-0" data-id="element-1101" data-mp="12pahya3cp86y">
                    <Link to={`/app/landings/${landing.id}/editor`} className="block truncate text-base font-semibold text-foreground hover:text-primary" data-id="element-1102" data-mp="12qlkk6l289wh">
                      {landing.name}
                    </Link>
                    <p className="mt-1 truncate text-sm text-muted-foreground" data-id="element-1103" data-mp="1vblg7e3hix0l">
                      /landing/{landing.slug}
                    </p>
                  </div>
                  <Dropdown align="right" trigger={<span className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-surface" data-id="element-1105" data-mp="1lhmjwvq3e7gk">
                        ⋯
                      </span>} items={[{
              label: 'Editar',
              icon: <PencilIcon className="h-4 w-4" data-id="element-1106" data-mp="a2pql9mvkw3e" />,
              onSelect: () => navigate(`/app/landings/${landing.id}/editor`)
            }, {
              label: 'Ver landing',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-1107" data-mp="uae89nxsxq41" />,
              onSelect: () => window.open(`/landing/${landing.slug}`, '_blank')
            }, {
              label: 'Copiar enlace',
              icon: <LinkIcon className="h-4 w-4" data-id="element-1108" data-mp="lh12vmijww12" />,
              onSelect: async () => {
                const ok = await copyToClipboard(landingUrl(landing.slug));
                if (ok) toast.success('Enlace copiado');else toast.error('No se ha podido copiar el enlace');
              }
            }, {
              label: 'Duplicar',
              icon: <CopyIcon className="h-4 w-4" data-id="element-1109" data-mp="146r9d6vof68t" />,
              onSelect: () => duplicateLanding(landing.id)
            }, {
              label: landing.status === 'publicada' ? 'Actualizar publicación' : 'Publicar',
              icon: <ExternalLinkIcon className="h-4 w-4" data-id="element-1110" data-mp="3oyj9jqml7wzw" />,
              onSelect: async () => {
                await setLandingStatus(landing.id, 'publicada');
                toast.success('Landing publicada correctamente', {
                  description: landingUrl(landing.slug)
                });
              }
            }, {
              label: 'Eliminar',
              icon: <Trash2Icon className="h-4 w-4" data-id="element-1111" data-mp="3jsxnsvcizqvi" />,
              destructive: true,
              onSelect: () => setToDelete(landing.id)
            }]} data-id="element-1104" data-mp="1leqjn0qrv6oy" />
                </div>

                <dl className="mt-5 grid grid-cols-2 gap-3 text-sm" data-id="element-1112" data-mp="3ol77vuqylwpv">
                  <div data-id="element-1113" data-mp="3skpv4x696jvv">
                    <dt className="text-xs text-muted-foreground" data-id="element-1114" data-mp="3snbzknxukj9v">Leads</dt>
                    <dd className="font-semibold text-foreground" data-id="element-1115" data-mp="1ctugx02n9zt">{landingLeads.length}</dd>
                  </div>
                  <div data-id="element-1116" data-mp="6bopqyf86r0t">
                    <dt className="text-xs text-muted-foreground" data-id="element-1117" data-mp="bcknsvcwbiyb">Visitas</dt>
                    <dd className="font-semibold text-foreground" data-id="element-1118" data-mp="bahzqgvazzw8">{landing.views}</dd>
                  </div>
                  <div data-id="element-1119" data-mp="2d47lfframc3r">
                    <dt className="text-xs text-muted-foreground" data-id="element-1120" data-mp="2d3fn3x6ba09s">Formulario</dt>
                    <dd className="truncate text-muted-foreground" data-id="element-1121" data-mp="2539mt9v7e6uk">
                      {form?.name ?? 'Sin asociar'}
                    </dd>
                  </div>
                  <div data-id="element-1122" data-mp="206j92t6vfncb">
                    <dt className="text-xs text-muted-foreground" data-id="element-1123" data-mp="2f1pg9jhpds78">Modificada</dt>
                    <dd className="text-muted-foreground" data-id="element-1124" data-mp="2ezmigdab6ury">{formatDate(landing.updatedAt)}</dd>
                  </div>
                </dl>

                <div className="mt-5 flex flex-wrap gap-2 border-t border-border pt-4" data-id="element-1125" data-mp="2o1vsl21o0m4t">
                  <Tag data-id="element-1126" data-mp="2j3kiqrbdebml">{STATUS_LABEL[landing.status]}</Tag>
                  <Tag data-id="element-1127" data-mp="2j30zn79zhh6z">{hasVideo ? 'Con vídeo' : 'Sin vídeo'}</Tag>
                </div>
              </li>;
      })}
        </ul>}

      <Modal open={creating} onClose={() => setCreating(false)} title="Nueva landing" footer={<div className="flex justify-end gap-2" data-id="element-1129" data-mp="2svgf9xxt5top">
            <Button variant="ghost" onClick={() => setCreating(false)} data-id="element-1130" data-mp="36twukd9qksig">
              Cancelar
            </Button>
            <Button onClick={handleCreate} loading={saving} disabled={!name.trim()} data-id="element-1131" data-mp="32suu5t432aew">
              Crear
            </Button>
          </div>} data-id="element-1128" data-mp="2st3xplvyplc6">
        <div className="space-y-5" data-id="element-1132" data-mp="3qeoooraotysp">
          <fieldset data-id="element-1133" data-mp="3qdlzqowxe6ff">
            <legend className="mb-2 text-sm font-medium text-foreground" data-id="element-1134" data-mp="3qe58bf0origs">
              Plantilla de partida
            </legend>
            <div className="grid gap-3 sm:grid-cols-2" data-id="element-1135" data-mp="3ch8gba8tzuyb">
              {LANDING_TEMPLATES.map(option => {
              const active = template === option.id;
              return <button key={option.id} type="button" onClick={() => setTemplate(option.id)} aria-pressed={active} className={`rounded-xl border p-4 text-left transition-colors ${active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`} data-id="element-1136" data-mp="32njccm3yhon5">
                    <span className="block text-sm font-semibold text-foreground" data-id="element-1137" data-mp="2xp81q95u7a3v">
                      {option.name}
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground" data-id="element-1138" data-mp="2jr06c5og8gut">
                      {option.description}
                    </span>
                  </button>;
            })}
            </div>
          </fieldset>
          <Input id="landing-name" label="Nombre interno" placeholder="Campaña inmobiliarias octubre" value={name} onChange={e => setName(e.target.value)} data-id="element-1139" data-mp="2z7q65fyqo4q3" />
          <Select id="landing-form" label="Formulario asociado" placeholder="Selecciona un formulario" options={forms.map(f => ({
          value: f.id,
          label: f.name
        }))} value={formId} onChange={e => setFormId(e.target.value)} data-id="element-1140" data-mp="2v4tl7ilq7wiz" />
        </div>
      </Modal>

      <ConfirmDialog open={Boolean(toDelete)} title="Eliminar landing" description="La página dejará de estar disponible públicamente. Esta acción no se puede deshacer." onCancel={() => setToDelete(null)} onConfirm={async () => {
      if (toDelete) await deleteLanding(toDelete);
      setToDelete(null);
    }} data-id="element-1141" data-mp="2ggyqjwo5ri39" />
    </>;
}